
/// <amd-module name="Release/Landing/HubContent/Releases/ActiveReleasesStore" />

import { IVssPageContext } from "VSS/Platform/Context";
import { format } from "VSS/Core/Util/String";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { getTFSPageData } from "Tfs/Platform/Page";

import { Release, DeploymentStatus, ReleaseStatus, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { StoreBase } from "DistributedTask/Flux/Store";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { getLatestDeploymentAttempt } from "Release/EnvironmentNode/StatusComputer";

import { ActiveReleasesActions, IActiveReleasesPayload, IUpdateReleasePayload, IRemoveReleasePayload, IActiveReleasesColumnPayload, IActiveReleasesColumnOptionsPayload, IUpdateReleaseEnvironmentStatusPayload, IStartedReleaseEnvironmentPayload } from "./ActiveReleasesActions";
import { saveContentToLocalStorage, getContentFromLocalStorage } from "Release/Helpers/BrowserStorage";
import { IReleasesFilterState } from "Release/Landing/HubContent/Filter/FilterTypes";
import { getPrimaryArtifactBranchName } from "Release/Landing/Artifact";

export interface IActiveReleasesState {
    definitionId: number;
    folderPath: string;
    releases: Release[];
    continuationToken: number;
    definitionEnvironmentCurrentReleaseMap: { [key: number]: number };
    isLoading: boolean;
    filterState?: IReleasesFilterState;
    showApprovalsBar: boolean;
    clearReleases: boolean;
    newlyCreatedReleaseId?: number;
}

export const ActiveReleaseColumnResizeEvent: string = "COLUMN_RESIZE_EVENT";
export const ActiveReleaseColumnOptionsModifiedEvent: string = "COLUMN_OPTIONS_EVENT";
export const FocusHoverEvent = "FOCUS_HOVER_EVENT";

export class ActiveReleasesStore extends StoreBase {
    public constructor(private _pageContext: IVssPageContext) {
        super("");
    }

    public static getKey(): string {
        return "release-landing-active-releases-store";
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);

        this._actionsHub = ActionsHubManager.GetActionsHub<ActiveReleasesActions>(ActiveReleasesActions);
        this._actionsHub.releasesUpdated.addListener(this._setReleases);
        this._actionsHub.releasesUpdatedWithMore.addListener(this._appendReleases);
        this._actionsHub.updateRelease.addListener(this._updateRelease);
        this._actionsHub.removeRelease.addListener(this._removeRelease);
        this._actionsHub.clearReleases.addListener(this._clearReleases);
        this._actionsHub.onEnvironmentColumnResize.addListener(this._onEnvironmentColumnResize);
        this._actionsHub.onReleaseColumnResize.addListener(this._onReleaseColumnResize);
        this._actionsHub.releaseCreated.addListener(this._releaseCreated);
        this._actionsHub.setColumnOptions.addListener(this._onColumnOptionsModified);
        this._actionsHub.clearApprovalsBar.addListener(this._onClearApprovalsBar);
        this._actionsHub.releaseFocused.addListener(this._onReleaseFocus);
        this._actionsHub.releaseHovered.addListener(this._onReleaseHover);
        this._actionsHub.updateReleaseEnvironmentStatus.addListener(this._onReleaseEnvironmentStatusUpdate);
        this._actionsHub.handleFetchedReleaseEnvironment.addListener(this._handleFetchedReleaseEnvironment);

        this._initializeState();
    }

    public getState(): IActiveReleasesState {
        return this._state;
    }

    public getEnvColumnWidth(definitionId: number): number {
        const storageKey: string = this._getColumnWidthMapKey(definitionId);
        const columnWidthMapString = getContentFromLocalStorage(storageKey);
        if (columnWidthMapString) {
            const columnWidthMap = JSON.parse(columnWidthMapString);
            if (columnWidthMap && columnWidthMap.hasOwnProperty("active-release-environment-column")) {
                return columnWidthMap["active-release-environment-column"];
            }
            else {
                return -1;
            }
        }
        else {
            return -1;
        }
    }

    public getReleaseColumnWidth(definitionId: number): number {
        const storageKey: string = this._getColumnWidthMapKey(definitionId);
        const columnWidthMapString = getContentFromLocalStorage(storageKey);

        if (columnWidthMapString) {
            const columnWidthMap = JSON.parse(columnWidthMapString);
            if (columnWidthMap && columnWidthMap.hasOwnProperty("active-release-name-column")) {
                return columnWidthMap["active-release-name-column"];
            }
            else {
                return -1;
            }
        }
        else {
            return -1;
        }
    }

    public showColumn(itemKey: number | string, columnKey: string): boolean {
        const storageKey: string = this._getColumnOptionsMapKey(itemKey);
        if (this._columnOptionsMap[storageKey] == undefined) {
            const settingsService = this._pageContext.getService<ISettingsService>("ISettingsService");
            this._columnOptionsMap[storageKey] = settingsService.getEntry<any>(storageKey, SettingsUserScope.Me);
        }

        return !!this._columnOptionsMap[storageKey] && this._columnOptionsMap[storageKey].findIndex((columnName) => columnName === columnKey) > -1;
    }

    public getFocusedReleaseId(): number {
        return this._focusedReleaseId;
    }

    public getHoveredReleaseId(): number {
        return this._hoveredReleaseId;
    }

    private _initializeState(): void {
        this._state = {
            definitionId: 0,
            folderPath: "\\",
            releases: [],
            continuationToken: 0,
            definitionEnvironmentCurrentReleaseMap: {},
            isLoading: true,
            showApprovalsBar: true,
            clearReleases: false
        };
    }

    private _setReleases = (payload: IActiveReleasesPayload): void => {
        // changing tabs should not render the bar if the user has dismissed it for the current definition
        const showApprovalsBar = this._state.showApprovalsBar || this._state.definitionId !== payload.definitionId;

        const envReleaseMap = payload.definitionEnvironmentCurrentReleaseMap || this._state.definitionEnvironmentCurrentReleaseMap || {};
        this._state = { ...payload, ...{ definitionEnvironmentCurrentReleaseMap: envReleaseMap, showApprovalsBar: showApprovalsBar, clearReleases: false } };
        this.emitChanged();
    }

    private _appendReleases = (payload: IActiveReleasesPayload): void => {

        this._state.releases.push(...(payload.releases || []));
        this._state.continuationToken = payload.continuationToken;
        this._state.isLoading = payload.isLoading;
        this.emitChanged();
    }

    private _updateRelease = (payload: IUpdateReleasePayload): void => {
        if (this._state.releases) {
            const index = this._state.releases.findIndex((release) => release.id === payload.release.id);
            if (index >= 0) {
                this._state.releases[index] = payload.release;
                this.emitChanged();
            }
        }
    }

    private _releaseCreated = (payload: IUpdateReleasePayload): void => {
        const release = payload.release;
        if (!!release && release.releaseDefinition.id === this._state.definitionId) {
            const isReleasePresent = this._state.releases.some(rel => rel.id === release.id);
            if (!isReleasePresent) {
                if (this._releaseMatchesFilters(release)) {
                    this._state.releases.unshift(release);
                    this.emitChanged();
                }
            }
        }
    }

    private _removeRelease = (payload: IRemoveReleasePayload): void => {
        if (this._state.releases) {
            const indexToRemove = this._state.releases.findIndex((release) => release.id === payload.releaseId);
            if (indexToRemove >= 0) {
                this._state.releases.splice(indexToRemove, 1);
                this.emitChanged();
            }
        }
    }

    private _clearReleases = (): void => {
        // Zero Releases view is different from the empty right panel which we want to show on clear releases trigger, 
        // hence using clearReleases in state instead of relying on releases length
        this._state.releases = [];
        this._state.clearReleases = true;
        this.emitChanged();
    }

    private _onClearApprovalsBar = (): void => {
        this._state.showApprovalsBar = false;
        this.emitChanged();
    }

    private _onReleaseFocus = (payload: number): void => {
        this._focusedReleaseId = payload;
        this.emit(FocusHoverEvent, {});
    }

    private _onReleaseHover = (payload: number): void => {
        this._hoveredReleaseId = payload;
        this.emit(FocusHoverEvent, {});
    }

    private _onReleaseEnvironmentStatusUpdate = (payload: IUpdateReleaseEnvironmentStatusPayload): void => {
        if (payload.definitionId === this._state.definitionId) {
            const release = this._state.releases.find(rel => rel.id === payload.releaseId);
            if (release) {
                const environment = release.environments.find(env => env.id === payload.environmentId);
                if (environment) {
                    environment.status = payload.environmentStatus;

                    const deploySteps = environment.deploySteps || [];
                    const step = getLatestDeploymentAttempt(deploySteps)

                    if (step) {
                        if (payload.deploymentStatus > DeploymentStatus.NotDeployed) {
                            this._state.definitionEnvironmentCurrentReleaseMap[environment.definitionEnvironmentId] = payload.releaseId;
                        }

                        if (step.operationStatus === DeploymentOperationStatus.Pending && payload.operationStatus !== DeploymentOperationStatus.Pending) {
                            environment.preDeployApprovals = [];
                            environment.postDeployApprovals = [];
                        }

                        step.status = payload.deploymentStatus;
                        step.operationStatus = payload.operationStatus;
                    }

                    this.emitChanged();
                }
            }
        }
    }

    private _handleFetchedReleaseEnvironment = (payload: IStartedReleaseEnvironmentPayload): void => {
        if (payload.definitionId === this._state.definitionId) {
            const release = this._state.releases.find(rel => rel.id === payload.environment.release.id);
            if (release) {
                const envIndex = release.environments.findIndex(env => env.id === payload.environment.id);

                // The environment should be present and the environment should have no deploy steps
                // If deploysteps are present, release environment status update will handle it
                if (envIndex >= 0 && !(payload.updateOnlyNotDeployed && release.environments[envIndex].deploySteps.length)) {
                    release.environments[envIndex] = payload.environment;

                    const step = getLatestDeploymentAttempt(payload.environment.deploySteps);
                    if (step && step.status > DeploymentStatus.NotDeployed) {
                        this._state.definitionEnvironmentCurrentReleaseMap[payload.environment.definitionEnvironmentId] = release.id;
                    }

                    this.emitChanged();
                }
            }
        }
    }

    private _onEnvironmentColumnResize = (payload: IActiveReleasesColumnPayload): void => {
        const storageKey: string = this._getColumnWidthMapKey(payload.definitionId);
        this._columnWidthMapForActiveReleasesView["active-release-environment-column"] = payload.newWidth;
        saveContentToLocalStorage(storageKey, JSON.stringify(this._columnWidthMapForActiveReleasesView));
        this.emit(ActiveReleaseColumnResizeEvent, this);
    }

    private _onReleaseColumnResize = (payload: IActiveReleasesColumnPayload): void => {
        const storageKey: string = this._getColumnWidthMapKey(payload.definitionId);
        this._columnWidthMapForActiveReleasesView["active-release-name-column"] = payload.newWidth;
        saveContentToLocalStorage(storageKey, JSON.stringify(this._columnWidthMapForActiveReleasesView));
        this.emit(ActiveReleaseColumnResizeEvent, this);
    }

    private _onColumnOptionsModified = (payload: IActiveReleasesColumnOptionsPayload): void => {
        const itemKey = payload.definitionId > 0 ? payload.definitionId : payload.folderPath;
        const storageKey: string = this._getColumnOptionsMapKey(itemKey);
        this._columnOptionsMap[storageKey] = payload.visibleColumns;

        this.emit(ActiveReleaseColumnOptionsModifiedEvent, this);
    }

    private _getColumnWidthMapKey(definitionId: number): string {
        const pageData = getTFSPageData(this._pageContext);
        const projectId = (pageData && pageData.project && pageData.project.id) || "";
        return (format("ActiveReleasesColumnWidthMapKeyForProjectId-{0}-AndRDId-{1}", projectId, definitionId.toString()));
    }

    private _getColumnOptionsMapKey(itemKey: number | string): string {
        const pageData = getTFSPageData(this._pageContext);
        const projectId = (pageData && pageData.project && pageData.project.id) || "";
        return (format("releases/activeDefinitions/{0}--{1}--visibleColumnOptions", projectId, itemKey.toString().replace(/\\/g, "$").replace(/[\s]+/g, "$$$")));
    }

    private _releaseMatchesFilters(release: Release): boolean {
        const filterState = this._state.filterState;
        if (filterState) {
            if (filterState.branch) {
                const branchName = getPrimaryArtifactBranchName(release.artifacts);
                if (branchName != filterState.branch) {
                    return false;
                }
            }

            if (filterState.createdBy && filterState.createdBy !== release.createdBy.id) {
                return false;
            }

            if (filterState.status && (filterState.status & release.status) === 0) {
                return false;
            }

            if (filterState.tags) {
                if (filterState.tags.some(tag => !release.tags.some(tag2 => tag2 === tag))) {
                    return false;
                }
            }
        }

        return true;
    }

    protected disposeInternal(): void {
        this._actionsHub.releasesUpdated.removeListener(this._setReleases);
        this._actionsHub.releasesUpdatedWithMore.removeListener(this._appendReleases);
        this._actionsHub.updateRelease.removeListener(this._updateRelease);
        this._actionsHub.removeRelease.removeListener(this._removeRelease);
        this._actionsHub.clearReleases.removeListener(this._clearReleases);
        this._actionsHub.onEnvironmentColumnResize.removeListener(this._onEnvironmentColumnResize);
        this._actionsHub.onReleaseColumnResize.removeListener(this._onReleaseColumnResize);
        this._actionsHub.releaseCreated.removeListener(this._releaseCreated);
        this._actionsHub.setColumnOptions.removeListener(this._onColumnOptionsModified);
        this._actionsHub.clearApprovalsBar.removeListener(this._onClearApprovalsBar);
        this._actionsHub.releaseFocused.removeListener(this._onReleaseFocus);
        this._actionsHub.releaseHovered.removeListener(this._onReleaseHover);
        this._actionsHub.updateReleaseEnvironmentStatus.removeListener(this._onReleaseEnvironmentStatusUpdate);
        this._actionsHub.handleFetchedReleaseEnvironment.addListener(this._handleFetchedReleaseEnvironment);
    }

    private _actionsHub: ActiveReleasesActions;
    private _focusedReleaseId: number = -1;
    private _hoveredReleaseId: number = -1;
    private _state: IActiveReleasesState;
    private _columnWidthMapForActiveReleasesView: { [key: string]: number } = {}; // Map to store the width corresponding to release/env column using separate custom strings as the key
    private _columnOptionsMap: { [key: string]: string[] } = {}; // Map between definitionId and visible columnKeys for optional columns(description and tags)
}
